import {t, Trans} from '@lingui/macro';
import {DateTime} from 'luxon';
import React, {FC, RefObject, useCallback, useRef, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {TextBox} from '../../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {IconButton} from '../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useDebounce} from '../../../../../../srcCommon/helpers/hookHelpers';
import {useLocalize} from '../../../../../../srcCommon/internationalization/internationalizationContext';
import {TimeFormatsEnum} from '../../../../../helpers/dateTimeHelpers';
import {TrueAvailabilityStatusesEnum} from '../../../../../helpers/models/schedulingLinkAvailabilityHelpers';
import {
  schedulingLinkAdvanceNoticeOptions,
  schedulingLinkMeetingBufferOptions,
  schedulingLinkMeetingDurationOptions,
  SchedulingLinkPropertiesEnum,
} from '../../../../../helpers/models/schedulingLinkHelpers';
import {useAppDispatch} from '../../../../../helpers/reduxHelpers';
import {ConferenceTypesEnum} from '../../../../../models/api/conferenceModel';
import {
  DraftSchedulingLinkModel,
  SchedulingLinkAvailabilityModel,
  SchedulingLinkMeetingPropertiesModel,
  SchedulingLinkStandardAvailabilityBlockModel,
} from '../../../../../models/api/schedulingLinkModel';
import {VisualParticipantModel} from '../../../../../models/api/visualParticipantModel';
import {
  InsertMeetingTimesAsyncActionResult,
  updateMeetingPropertiesAction,
} from '../../../../../store/panels/calendars/scheduling/calendarsPanelSchedulingActions';
import {CalendarsPanelId} from '../../../../../store/panels/panelStateBase';
import {Localizer} from '../../../../../store/stateless/internationalizationSelectors';
import {SelectedCalendarData} from '../../../../calendars/editor/calendarDropdownBox';
import {CalendarDropdownBoxContainer} from '../../../../calendars/editor/calendarDropdownBoxContainer';
import {ConferenceDropdownBoxContainer} from '../../../../calendars/editor/conferenceEditor/conferenceDropdownBoxContainer';
import {VisualParticipantsDropdownBox} from '../../../../calendars/editor/participantsEditor/visualParticipantsDropdownBox';
import {SideCalendarsHeader} from '../../../../calendars/header/sideCalendarsHeader';
import {CalendarEventParticipantsList} from '../../../../calendars/participants/calendarEventParticipantsList';
import {
  schedulingLinkPropertyPlaceholders,
  schedulingLinkPropertyTooltips,
} from '../../../../calendars/scheduling/schedulingLinkViewHelpers';
import {DurationDropdownBox} from '../../../../calendars/scheduling/settings/durationDropdownBox';
import {SchedulingLinkBoxedInputWrapper} from '../../../../calendars/scheduling/settings/schedulingLinkBoxedInputWrapper';
import {CalendarEventDetailRow} from '../../../../calendars/viewer/calendarEventDetailRow';
import {HtmlEditorBoxHandle} from '../../../../common/boxedInputs/htmlEditorBox';
import {HtmlEditorBoxContainer} from '../../../../common/boxedInputs/htmlEditorBoxContainer';
import {DynamicScrollBoundaries} from '../../../../common/scroll/dynamicScrollBoundaries';
import {MeetingFormattingSettings} from './meetingFormattingSettings';
import {MeetingSettingsPanelFooter} from './meetingSettingsPanelFooter';

/*
 * Types.
 */

export interface MeetingTimesData {
  availability: SchedulingLinkAvailabilityModel;
  linkId: number;
  bookingUrl: string;
  timezone: string;
  timeFormat: TimeFormatsEnum;
}

export interface MeetingSettingsPanelPublicProps {
  onRequestClose: Handler;
  onSelectMeetingTimes: HandlerOf<MeetingTimesData>;
  panelId: CalendarsPanelId;
}

interface MeetingSettingsPanelProps extends MeetingSettingsPanelPublicProps {
  selectedCalendarData: SelectedCalendarData;
  selectedSlots: ReadonlyArray<SchedulingLinkStandardAvailabilityBlockModel>;
  onClearSlots: Handler;
  trueAvailabilityStatus: TrueAvailabilityStatusesEnum;
  onInsertMeetingTimes: () => Promise<InsertMeetingTimesAsyncActionResult>;
  draftLink: DraftSchedulingLinkModel;
  isPopout: boolean;
  onUpdateSchedulingLink: HandlerOf<Partial<DraftSchedulingLinkModel>>;
  onUpdateCalendarVersionId: HandlerOf<number>;
  visualParticipants: ReadonlyArray<VisualParticipantModel>;
  onUpdateMeetingDuration: HandlerOf<number>;
  initialTimeFormat: TimeFormatsEnum;
  onAddAdditionalGuests: HandlerOf<ReadonlyArray<string>>;
  calendarNames: ReadonlyArray<string>;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  grid-template-rows: auto 1fr auto;
  display: grid;
  overflow: hidden;
`;

const StyledContentDiv = styled.div`
  padding: 2px 15px 0px 15px;
  display: grid;
  grid-auto-rows: min-content;
  grid-row-gap: 12px;
  overflow-y: scroll;
`;

const StyledSectionIcon = styled(Icon)`
  margin-top: 7px;
`;

const StyledButtonWrapperDiv = styled.div`
  justify-self: flex-start;
`;

const StyledDropdownWrapperDiv = styled.div`
  > div {
    padding: 0px;
  }
`;

const StyledHtmlEditorBoxContainer = styled(HtmlEditorBoxContainer)`
  min-height: 70px;
  word-break: break-word;
`;

const StyledVisualParticipantsDropdownBox = styled(VisualParticipantsDropdownBox)`
  margin: 0;
`;

const StyledGuestListWrapper = styled.div`
  margin-top: -4px;
  margin-left: 28px;
  margin-bottom: 8px;
`;

const StyledMeetingFormattingSettingsWrapperDiv = styled.div`
  padding-top: 15px;
`;

/*
 * Component.
 */

export const MeetingSettingsPanel: FC<MeetingSettingsPanelProps> = (props) => {
  const theme = useUserTheme();
  const localize = useLocalize();
  const dispatch = useAppDispatch();

  const {draftLink, panelId, initialTimeFormat} = props;

  const editorRef = useRef<HtmlEditorBoxHandle>(null);

  const [isShowingMore, setIsShowingMore] = useState<boolean>(() =>
    // If any of these are set, open the show more section initially.
    Boolean(
      draftLink.meetingProperties.description ||
        draftLink.meetingBufferMinutes ||
        draftLink.advanceNoticeMinutes,
    ),
  );
  const [timezone, setTimezone] = useState<string>(() => DateTime.local().zoneName);
  const [timeFormat, setTimeFormat] = useState<TimeFormatsEnum>(initialTimeFormat);

  const onUpdateMeetingProperties = useCallback(
    (changes: Partial<SchedulingLinkMeetingPropertiesModel>) =>
      dispatch(updateMeetingPropertiesAction(panelId, changes)),
    [dispatch, panelId],
  );

  const onSummaryChangeDebounced = useDebounce(
    (summary: string) => onUpdateMeetingProperties({summary}),
    100,
  );

  const onLocationChangeDebounced = useDebounce(
    (location: string) => onUpdateMeetingProperties({location}),
    100,
  );

  const onDescriptionChangeDebounced = useDebounce(
    (description: string) => onUpdateMeetingProperties({description}),
    100,
  );

  const onSaveClick = async () => {
    onSummaryChangeDebounced.flush();
    onLocationChangeDebounced.flush();
    onDescriptionChangeDebounced.flush();

    const {hasAvailability, availability, schedulingLink} = await props.onInsertMeetingTimes();
    if (hasAvailability && availability && schedulingLink) {
      props.onSelectMeetingTimes({
        linkId: schedulingLink.id,
        availability,
        timezone,
        bookingUrl: schedulingLink.bookingPageUrl,
        timeFormat,
      });
    }
  };

  return (
    <StyledWrapperDiv>
      <SideCalendarsHeader title={<Trans id="calendar-header-meetingsettings">Meeting settings</Trans>}>
        {!props.isPopout && (
          <IconButton
            iconName="close"
            onClick={props.onRequestClose}
            label={<Trans id="calendar-header-close">Close</Trans>}
          />
        )}
      </SideCalendarsHeader>
      <DynamicScrollBoundaries hasTopBoundary hasBottomBoundary color={theme.greys.shade30}>
        <StyledContentDiv>
          {renderSummaryInput(localize, props, onSummaryChangeDebounced)}
          {renderDurationSection(props)}
          {renderConferenceTypeSection(props, (conferenceType) =>
            onUpdateMeetingProperties({conferenceType}),
          )}
          {renderLocationSection(localize, props, onLocationChangeDebounced)}
          {renderCalendarSection(props)}
          {renderGuestsSection(localize, props, (additionalGuests) =>
            onUpdateMeetingProperties({additionalGuests}),
          )}
          {maybeRenderDescriptionSection(
            localize,
            props,
            isShowingMore,
            editorRef,
            onDescriptionChangeDebounced,
          )}
          {maybeRenderMeetingBufferSection(isShowingMore, props)}
          {maybeRenderAdvancedNoticeSection(isShowingMore, props)}
          {renderMoreSettingsButton(props, isShowingMore, setIsShowingMore)}
          <StyledMeetingFormattingSettingsWrapperDiv>
            <MeetingFormattingSettings
              timezone={timezone}
              timeFormat={timeFormat}
              onTimezoneSelect={setTimezone}
              onTimeFormatSelect={setTimeFormat}
            />
          </StyledMeetingFormattingSettingsWrapperDiv>
        </StyledContentDiv>
      </DynamicScrollBoundaries>
      <MeetingSettingsPanelFooter
        selectedSlots={props.selectedSlots}
        draftLink={draftLink}
        onSaveClick={onSaveClick}
        onClearClick={props.onClearSlots}
        trueAvailabilityStatus={props.trueAvailabilityStatus}
        calendarNames={props.calendarNames}
      />
    </StyledWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderSummaryInput(
  localize: Localizer,
  props: MeetingSettingsPanelProps,
  onSummaryChange: HandlerOf<string>,
) {
  return (
    <SchedulingLinkBoxedInputWrapper>
      <TextBox
        size={VisualSizesEnum.LARGE}
        placeholder={schedulingLinkPropertyPlaceholders[SchedulingLinkPropertiesEnum.TITLE](localize)}
        defaultValue={props.draftLink.meetingProperties.summary}
        onChange={onSummaryChange}
        shouldAutoFocus
      />
    </SchedulingLinkBoxedInputWrapper>
  );
}

function renderDurationSection(props: MeetingSettingsPanelProps) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconContent={<StyledSectionIcon name="snoozeSmall" color={theme.greys.shade70} />}
          content={
            <StyledDropdownWrapperDiv>
              <DurationDropdownBox
                duration={props.draftLink.meetingProperties.durationMinutes}
                onDurationSelect={(durationMinutes) => props.onUpdateMeetingDuration(durationMinutes)}
                options={schedulingLinkMeetingDurationOptions}
              />
            </StyledDropdownWrapperDiv>
          }
        />
      )}
    </UserThemeConsumer>
  );
}

function renderConferenceTypeSection(
  props: MeetingSettingsPanelProps,
  onConferenceChange: HandlerOf<ConferenceTypesEnum | undefined>,
) {
  const {supportedProviderConferenceTypes} = props.selectedCalendarData.calendar;
  const {calendarVersion} = props.selectedCalendarData;

  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconContent={<StyledSectionIcon name="videoSmall" color={theme.greys.shade70} />}
          content={
            <StyledDropdownWrapperDiv>
              <ConferenceDropdownBoxContainer
                supportedProviderConferenceTypes={supportedProviderConferenceTypes}
                conferenceType={props.draftLink.meetingProperties.conferenceType}
                onConferenceTypeChange={(conferenceType) => onConferenceChange(conferenceType)}
                calendarVersionId={calendarVersion.id}
                isEditing={false}
              />
            </StyledDropdownWrapperDiv>
          }
        />
      )}
    </UserThemeConsumer>
  );
}

function renderLocationSection(
  localize: Localizer,
  props: MeetingSettingsPanelProps,
  onLocationChange: HandlerOf<string>,
) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconContent={<StyledSectionIcon name="locationSmall" color={theme.greys.shade70} />}
          content={
            <SchedulingLinkBoxedInputWrapper>
              <TextBox
                size={VisualSizesEnum.MEDIUM}
                placeholder={schedulingLinkPropertyPlaceholders[SchedulingLinkPropertiesEnum.LOCATION](
                  localize,
                )}
                defaultValue={props.draftLink.meetingProperties.location}
                onChange={onLocationChange}
              />
            </SchedulingLinkBoxedInputWrapper>
          }
        />
      )}
    </UserThemeConsumer>
  );
}

function renderCalendarSection(props: MeetingSettingsPanelProps) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconContent={<StyledSectionIcon name="calendarSmall" color={theme.greys.shade70} />}
          content={
            <CalendarDropdownBoxContainer
              selectedCalendarVersion={props.selectedCalendarData.calendarVersion}
              onCalendarDataSelect={(data) => props.onUpdateCalendarVersionId(data.calendarVersion.id)}
            />
          }
        />
      )}
    </UserThemeConsumer>
  );
}

function renderGuestsSection(
  localize: Localizer,
  props: MeetingSettingsPanelProps,
  onGuestsChange: HandlerOf<ReadonlyArray<string>>,
) {
  const hasGuests = props.visualParticipants.length > 0;
  return (
    <>
      <UserThemeConsumer>
        {(theme) => (
          <CalendarEventDetailRow
            iconContent={<StyledSectionIcon name="teammatesSmall" color={theme.greys.shade70} />}
            content={
              <StyledVisualParticipantsDropdownBox
                panelId={props.panelId}
                hasExistingAttendees={hasGuests}
                onVisualParticipantsSelect={(newVisualParticipants) =>
                  props.onAddAdditionalGuests(newVisualParticipants.map((vp) => vp.contact.handle))
                }
                organizerAddress={props.selectedCalendarData.calendar.organizerAddress}
                isUnsavedDraft
                placeholder={localize(
                  t('scheduling-meetingsettings-additionalguests')`Add additional guests`,
                )}
              />
            }
            renderTooltip={() =>
              schedulingLinkPropertyTooltips[SchedulingLinkPropertiesEnum.ADDITIONAL_GUESTS]
            }
          />
        )}
      </UserThemeConsumer>
      {hasGuests && (
        <StyledGuestListWrapper>
          <CalendarEventParticipantsList
            panelId={props.panelId}
            visualParticipants={props.visualParticipants}
            onVisualParticipantsChange={(participants) =>
              onGuestsChange(participants.map((vp) => vp.contact.handle))
            }
            shouldDisableTypeButton
          />
        </StyledGuestListWrapper>
      )}
    </>
  );
}

function maybeRenderDescriptionSection(
  localize: Localizer,
  props: MeetingSettingsPanelProps,
  isShowingMore: boolean,
  editorRef: RefObject<HtmlEditorBoxHandle>,
  onDescriptionChange: HandlerOf<string>,
) {
  if (!isShowingMore) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconContent={<StyledSectionIcon name="notesSmall" color={theme.greys.shade70} />}
          content={
            <SchedulingLinkBoxedInputWrapper>
              <StyledHtmlEditorBoxContainer
                ref={editorRef}
                initialCleanHtml={props.draftLink.meetingProperties.description}
                renderPlaceholder={() =>
                  schedulingLinkPropertyPlaceholders[SchedulingLinkPropertiesEnum.DESCRIPTION](localize)
                }
                hasToolbar
                onChange={() => {
                  const editor = editorRef.current;
                  if (!editor) {
                    return;
                  }

                  onDescriptionChange(!editor.isEmpty ? editor.cleanHtml : '');
                }}
              />
            </SchedulingLinkBoxedInputWrapper>
          }
        />
      )}
    </UserThemeConsumer>
  );
}

function maybeRenderMeetingBufferSection(isShowingMore: boolean, props: MeetingSettingsPanelProps) {
  if (!isShowingMore) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconContent={<StyledSectionIcon name="meetingBufferSmall" color={theme.greys.shade70} />}
          content={
            <StyledDropdownWrapperDiv>
              <DurationDropdownBox
                duration={props.draftLink.meetingBufferMinutes || 0}
                onDurationSelect={(meetingBufferMinutes) =>
                  props.onUpdateSchedulingLink({meetingBufferMinutes})
                }
                options={schedulingLinkMeetingBufferOptions}
                renderNullValue={() => <Trans id="scheduling-meetingbuffer-none">No meeting buffer</Trans>}
              />{' '}
            </StyledDropdownWrapperDiv>
          }
          renderTooltip={() => schedulingLinkPropertyTooltips[SchedulingLinkPropertiesEnum.MEETING_BUFFER]}
        />
      )}
    </UserThemeConsumer>
  );
}

function maybeRenderAdvancedNoticeSection(isShowingMore: boolean, props: MeetingSettingsPanelProps) {
  if (!isShowingMore) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconContent={<StyledSectionIcon name="advancedNoticeSmall" color={theme.greys.shade70} />}
          content={
            <StyledDropdownWrapperDiv>
              <DurationDropdownBox
                duration={props.draftLink.advanceNoticeMinutes || 0}
                onDurationSelect={(advanceNoticeMinutes) =>
                  props.onUpdateSchedulingLink({advanceNoticeMinutes})
                }
                options={schedulingLinkAdvanceNoticeOptions}
                renderNullValue={() => <Trans id="scheduling-advancednotice-none">No advanced notice</Trans>}
              />
            </StyledDropdownWrapperDiv>
          }
          renderTooltip={() => schedulingLinkPropertyTooltips[SchedulingLinkPropertiesEnum.ADVANCE_NOTICE]}
        />
      )}
    </UserThemeConsumer>
  );
}

function renderMoreSettingsButton(
  props: MeetingSettingsPanelProps,
  isShowingMore: boolean,
  setIsShowingMore: HandlerOf<boolean>,
) {
  if (isShowingMore) {
    return (
      <StyledButtonWrapperDiv>
        <Button variant={ButtonVariantsEnum.LINK} onClick={() => setIsShowingMore(false)}>
          <Trans id="scheduling-meetingsettings-seeless">See less</Trans>
        </Button>
      </StyledButtonWrapperDiv>
    );
  }

  return (
    <StyledButtonWrapperDiv>
      <Button variant={ButtonVariantsEnum.LINK} onClick={() => setIsShowingMore(true)}>
        <Trans id="scheduling-meetingsettings-seemore">See more</Trans>
      </Button>
    </StyledButtonWrapperDiv>
  );
}
